/**
 * Format price with 5 significant figures and minimum 2 decimal places
 * Trailing zeros are removed (except to maintain minimum 2 decimal places)
 * Examples:
 * - 1234.567 -> 1234.57 (5 sig figs, 2 decimals)
 * - 12.34567 -> 12.346 (5 sig figs, 3 decimals)
 * - 0.123456 -> 0.12346 (5 sig figs, 5 decimals)
 * - 0.00123456 -> 0.0012346 (5 sig figs, 7 decimals)
 * - 0.0030400 -> 0.00304 (trailing zeros removed)
 * - 100 -> 100.00 (minimum 2 decimals)
 */
export function formatPrice(price: number): string {
  // Handle null, undefined, and invalid numbers
  if (price == null || !isFinite(price) || isNaN(price)) {
    return '0.00';
  }

  // Handle zero
  if (price === 0) {
    return '0.00';
  }

  const absPrice = Math.abs(price);
  
  // Handle extremely small numbers (treat as zero)
  if (absPrice < 1e-20) {
    return '0.00';
  }
  
  // Calculate number of significant figures
  const magnitude = Math.floor(Math.log10(absPrice));
  
  // For prices >= 1, we want 5 significant figures but at least 2 decimal places
  if (absPrice >= 1) {
    // Calculate decimal places needed for 5 sig figs
    const sigFigDecimals = Math.max(0, 4 - magnitude);
    // Use at least 2 decimal places, but cap at 20 to avoid toFixed errors
    const decimals = Math.min(20, Math.max(2, sigFigDecimals));
    const formatted = price.toFixed(decimals);
    return removeTrailingZeros(formatted, 2);
  }
  
  // For prices < 1, we want 5 significant figures after the first non-zero digit
  // Calculate how many decimal places we need, but cap at 20
  const decimals = Math.min(20, Math.max(2, -magnitude + 4));
  const formatted = price.toFixed(decimals);
  // For prices < 1, don't enforce minimum decimals to preserve significant figures
  return removeTrailingZeros(formatted, 0);
}

/**
 * Remove trailing zeros from a formatted number string
 * but keep at least minDecimals decimal places
 * For numbers < 1, minDecimals should be 0 to allow proper significant figure display
 */
function removeTrailingZeros(numStr: string, minDecimals: number = 2): string {
  // Split into integer and decimal parts
  const parts = numStr.split('.');
  if (parts.length !== 2 || !parts[1]) {
    return numStr;
  }
  
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Remove trailing zeros
  let trimmedDecimals = decimalPart.replace(/0+$/, '');
  
  // Ensure we have at least minDecimals decimal places
  // But if minDecimals is 0, we can remove all trailing zeros
  if (minDecimals > 0 && trimmedDecimals.length < minDecimals) {
    trimmedDecimals = decimalPart.substring(0, minDecimals);
  }
  
  // If no decimal part remains, ensure we still have the decimal point with zeros if minDecimals > 0
  if (trimmedDecimals.length === 0 && minDecimals > 0) {
    trimmedDecimals = '0'.repeat(minDecimals);
  }
  
  return `${integerPart}.${trimmedDecimals}`;
}
